import Text from 'components/Text/Text';
import React, { useContext, useEffect } from 'react';
import { ICreateModifierContext, CreateModifierContext } from '../../../../createModifierState/createModifierContext';
import { CustomerSelect } from './components/CustomerSelect';
import { RestaurantsMultipleSelect } from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import { NameModifierInput } from './components/NameModifierInput';
import { HasSchedulePriorityCheckBox } from './components/HasSchedulePriorityCheckBox';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { BtnBlock } from './components/BtnBlock';
import { Form } from 'antd';
import { preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Button from 'components/Button/Button';
import { DescriptionField } from './components/DescriptionField';
import { ExternalIdField } from './components/ExternalIdField';

export const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setIDCustomer,
		setFetchedCustomers,
		setIDsRestaurant,
		setFetchedRestaurants,
		changeNameOfModifier,
		changeDescription,
		changeExternalId,
		generateUniqueExternalId,
		toggleHasSchedulePriority,
		setFetchedTranslations,
		setTranslationTab,
		changeNameTranslation,
		changeDescriptionTranslation,
		changeActivationSchedule,
		changeDeactivationSchedule,
		fileId,
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		nameOfModifier,
		description,
		externalId,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		translations,
		defaultCustomerLanguage,
		selectedTranslationTab,
	} = useContext<ICreateModifierContext>(CreateModifierContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			IDSelectedCustomer,
			IDsSelectedRestaurant,
			nameOfModifier,
			description,
			externalId,
			hasSchedulePriority,
			activationSchedule,
			deactivationSchedule,
		});
	}, [
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		nameOfModifier,
		description,
		externalId,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		setDataForSubmit({
			name: nameOfModifier,
			description: description || null,
			customerId: IDSelectedCustomer ? IDSelectedCustomer : '',
			externalId: externalId || null,
			fileId: fileId,
			restaurantIds: IDsSelectedRestaurant,
			overrideScheduleAvailability: hasSchedulePriority,
			activationCron: activationSchedule || null,
			deactivationCron: deactivationSchedule || null,
			translations,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<NameModifierInput changeNameOfModifier={changeNameOfModifier} />
					<DescriptionField changeDescription={changeDescription} />
					<CustomerSelect
						setIDCustomer={setIDCustomer}
						setFetchedCustomers={setFetchedCustomers}
						IDSelectedCustomer={IDSelectedCustomer}
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="translationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<MenuTranslationForm
						className={style.translationForm}
						setFetchedTranslations={setFetchedTranslations}
						setTranslationTab={setTranslationTab}
						changeNameTranslation={changeNameTranslation}
						changeDescriptionTranslation={changeDescriptionTranslation}
						isShowLanguageName={false}
						IDSelectedCustomer={IDSelectedCustomer}
						translations={translations}
						selectedTranslationTab={selectedTranslationTab}
						nameLabel="nameModifierLabel"
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<HasSchedulePriorityCheckBox
						toggleHasSchedulePriority={toggleHasSchedulePriority}
						hasSchedulePriority={hasSchedulePriority}
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantListHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<RestaurantsMultipleSelect
						setIDsRestaurant={setIDsRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
						IDSelectedCustomer={IDSelectedCustomer}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};
